import { createContext, useContext } from 'react';

/** Things that can be opened from anywhere in the application. */
export type Destination = 'component' | 'location' | 'purchase' | 'project' | 'spec' | 'history';

export interface Navigation {
  /** Switch to the screen that shows this record, with it selected.
   *
   *  For `history` the identifier is the record's own id and `entityType` names
   *  its table, because the audit log is indexed by the pair — "component 7"
   *  and "location 7" are different rows of different tables. */
  open: (destination: Destination, id: string, entityType?: string) => void;
  /** Whether that screen exists yet. A chip that cannot lead anywhere should
   *  not look like a link. */
  canOpen: (destination: Destination) => boolean;
}

/**
 * Opening a record from a screen that has nothing to do with it.
 *
 * A movement in a component's history points at the order it came from, and the
 * order lives four levels up and one section across. Passing a callback down
 * that chain would mean every component in between carrying a prop it does not
 * use — and the next such link would mean threading it again.
 *
 * A context instead, filled in by the shell, which is the only place that knows
 * what a section is.
 */
const NavigationContext = createContext<Navigation>({
  open: () => undefined,
  canOpen: () => false,
});

export const NavigationProvider = NavigationContext.Provider;

export const useNavigation = (): Navigation => useContext(NavigationContext);
